// Mobile redeem landing page.
//
// Reached when a user scans the QR shown next to a claimed code on
// the desktop beta page. The page tries to deeplink into the
// installed app (`gntl://redeem/<code>`); if the app isn't installed
// or the deeplink doesn't fire, the code is shown plainly with a
// copy button as a fallback.
//
// Once the mobile app ships an Android App Link intent filter on
// `https://beta.gntl.app/r/*`, the OS will route this URL straight
// into the app and skip the page entirely. Until then, this page is
// the bridge.

import { useEffect, useState } from "react";

const APP_SCHEME_PREFIX = "gntl://redeem/";

export function RedeemLanding({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const upper = code.toUpperCase();

  useEffect(() => {
    // Best-effort: try to open the app immediately. If the scheme
    // isn't registered, the browser silently does nothing — no harm.
    // We do this once, just on first paint, so the user sees the
    // fallback if it didn't work.
    window.location.assign(APP_SCHEME_PREFIX + upper);
  }, [upper]);

  function copy() {
    navigator.clipboard.writeText(upper).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-[var(--color-cream)]">
      <h1 className="text-2xl font-bold text-[var(--color-ink)]">
        Your beta code
      </h1>
      <p className="mt-1 text-sm text-[var(--color-muted)]">
        Tap to copy, then open the app and paste in Settings → Redeem Code.
      </p>

      <button
        type="button"
        onClick={copy}
        className="mt-6 font-mono text-3xl font-bold tracking-[8px] text-[var(--color-ink)] bg-white border-[1.5px] border-dashed border-zinc-300 rounded-xl py-4 px-6 hover:border-zinc-400 transition"
      >
        {upper}
      </button>
      {copied && (
        <span className="mt-2 text-xs font-medium text-emerald-600">
          Copied!
        </span>
      )}

      <a
        href={APP_SCHEME_PREFIX + upper}
        className="mt-6 px-5 py-3 bg-[var(--color-accent)] text-white font-semibold rounded-xl no-underline hover:bg-blue-900"
      >
        Open in the app →
      </a>
      <p className="mt-3 text-xs text-[var(--color-muted)] max-w-xs">
        If the app doesn't open automatically, install it first, then come
        back and tap the button above.
      </p>
    </div>
  );
}
